import type { ExternalConfigType } from "../../../shared-logic/types/ExternalPriceType";
import type { CommonSettingsType } from "../../../../common-deprecated/settings/fetchCommonSettings";
import type {
    CCGetMonthlyPaymentResponseType,
    CCGetMonthlyRatesResponseType,
    ParsedMonthlyRateType,
    SaveAndPrintResponseType,
} from "../../../../common-deprecated/types/FinanceInsuranceRateTypes";
import type { CarConfigSettingsType } from "../redux/slices/CarConfigSettingsSlice";
import type { CarType } from "../../../shared-logic/types/CarType";
import { AemFmComponent } from "../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import { showAemMonthlyRates } from "../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import {
    formatCarFinance,
    formatLegalText,
    processItemRates,
} from "../../../shared-logic/utils/formatters/formatFinance";

/**
 * ShowMonthlyRates setting for Carconfig.
 */
export const showCarConfigMonthlyRate = (commonSettings: CommonSettingsType, car?: CarType): boolean => {
    const showRates = showAemMonthlyRates(commonSettings, AemFmComponent.Carconfig, "new", car?.model.internalCode);

    return (
        showRates &&
        (!car || !car.id || Object.keys(car).length === 0 || (car.availability && car.availability.showMonthlyRate))
    );
};

/**
 * Method which checks if the monthly rates for configurables should be loaded or not
 */
export const showAccessoriesRate = (
    settings: CommonSettingsType,
    carConfigSettings: CarConfigSettingsType,
): boolean => {
    return carConfigSettings.showAccessoriesRate && showCarConfigMonthlyRate(settings);
};

export const fetchMonthlyRate = async (
    settings: CommonSettingsType,
    carConfigSettings: CarConfigSettingsType,
    config: ExternalConfigType,
    selectedItems?: string[] | null,
    unSelectedItems?: string[] | null,
): Promise<ParsedMonthlyRateType | undefined> => {
    /**
     * Total monthly rate fetching
     */
    const monthlyPaymentResponse = await fetch(carConfigSettings.monthlyRateService, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(config),
    });

    const monthlyRateResult: CCGetMonthlyPaymentResponseType = await monthlyPaymentResponse.json();

    const formattedResults = formatCarFinance(monthlyRateResult);

    let resultValue;
    if (formattedResults) {
        /**
         * Legal disclaimers
         * Do not load legal text if we don't have quotes to start with, this is also part of the response's info object
         * since this is being passed as is until it's used in the (priceUtils) formatMonthlyPrice function.
         */
        if (formattedResults?.quoteInfo) {
            const legalTextBody = {
                ...config,
                Legal_Text_ID: monthlyRateResult?.Quotation?.Legal_Text_ID || "",
                Execution_ID: monthlyRateResult?.Quotation?.Execution_ID || "",
            };

            // Fetch the legal disclaimers for the current finance
            const monthlyDisclaimerResponse = await fetch(carConfigSettings.monthlyRateDisclaimerService, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(legalTextBody),
            });

            const montlyDisclaimerResults = monthlyDisclaimerResponse.ok && (await monthlyDisclaimerResponse.json());

            formattedResults.info.disclaimer = formatLegalText(montlyDisclaimerResults);
        }

        /**
         * individual monthly rate fetching
         */
        if (showAccessoriesRate(settings, carConfigSettings) && formattedResults) {
            const accessoriesRateConfig = {
                ...config,
                SelectedItems: selectedItems,
                UnselectedItems: unSelectedItems,
            };

            // Fetch the monthly rate for all selected items.
            const monthlySelectedConfigurablesResponse = await fetch(carConfigSettings.monthlyRateSelectedService, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(accessoriesRateConfig),
            });

            // Fetch the monthly rate for all unselected items.
            const monthlyUnselectedConfigurablesResponse = await fetch(carConfigSettings.monthlyRateUnselectedService, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(accessoriesRateConfig),
            });

            const selectedMonthlyRates: SaveAndPrintResponseType = await monthlySelectedConfigurablesResponse.json();
            const unSelectedMonthlyRates: CCGetMonthlyRatesResponseType =
                await monthlyUnselectedConfigurablesResponse.json();

            (formattedResults as ParsedMonthlyRateType).unselectedItemPrices = unSelectedMonthlyRates
                ? processItemRates<CCGetMonthlyRatesResponseType>(unSelectedMonthlyRates, "UnselectedItems")
                : [];
            (formattedResults as ParsedMonthlyRateType).selectedItemPrices = selectedMonthlyRates
                ? processItemRates<SaveAndPrintResponseType>(selectedMonthlyRates, "SelectedItems")
                : [];
        }

        resultValue = formattedResults as ParsedMonthlyRateType;
    }

    return resultValue;
};
